import { LoggedUser } from '../models/LoggedUser'
import { Container, IInit } from '../../../common/container/Container'
import { AuthDTO } from '../models/AuthDTO'
import { ConnectableObservable, Observable, Subject } from 'rxjs'
import { IUserApi } from '../api/UserApi'
import { IStatusService } from '../../../common/status/StatusService'
import { fromModel, LoggedUserDTO, toModel } from '../models/LoggedUserDTO'
import { multicast } from 'rxjs/operators'
import { ICircleService } from './CircleService'
import { CIRCLE_SERVICE_KEY } from '../container'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import { LoginResult } from '../models/LoginResult'

export interface ILoggedUserService extends IInit {
  login(a: AuthDTO): Observable<LoginResult>

  logout(): void

  getObservable(): Observable<LoggedUser | undefined>

  get(): LoggedUser | undefined

  setRoles(roles: string[], permissions: string[]): void

  sendRecoverPassword(email: string): Observable<boolean>
}

const LOGGED_USER_KEY = 'logged user'

type Props = {
  apiKey: symbol
}

export class LoggedUserService implements ILoggedUserService {
  private readonly _apiKey: symbol
  private _container!: Container
  private _api!: IUserApi
  private _circleService!: ICircleService
  private _statusService!: IStatusService

  private _user: LoggedUser | undefined
  private _userSubject = new Subject<LoggedUser>()

  constructor(p: Props) {
    this.loadUser()
    this._apiKey = p.apiKey
  }

  init(c: Container) {
    this._container = c
    this._api = this._container.get<IUserApi>(this._apiKey)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._circleService = this._container.get<ICircleService>(CIRCLE_SERVICE_KEY)
  }

  login(a: AuthDTO): Observable<LoginResult> {
    const multi = this._api
      .login(a)
      .pipe(multicast(() => new Subject<LoginResult>())) as ConnectableObservable<LoginResult>
    multi.connect()
    multi.subscribe((res) => {
      if (res.user) {
        this.storeUser(res.user)
      }
    })

    return multi
  }

  logout() {
    this._api.logout().subscribe(() => {
      this.removeUser()
      this._circleService.setActive(undefined)
    })
  }

  get(): LoggedUser | undefined {
    return this._user
  }

  getObservable(): Observable<LoggedUser | undefined> {
    return this._userSubject.pipe()
  }

  setRoles(roles: string[], permissions: string[]) {
    if (!this._user) {
      return
    }

    this._user.roles = roles
    this._user.permissions = permissions
    this.storeUser(fromModel(this._user))
  }

  private next() {
    this._userSubject.next(this._user)
  }

  private loadUser() {
    const dto = (JSON.parse(localStorage.getItem(LOGGED_USER_KEY) || 'null') ||
      undefined) as LoggedUserDTO
    if (dto && new Date(dto.sessionExpires) > new Date()) {
      this._user = toModel(dto)
      this.next()
      setTimeout(() => this.logout(), this._user.sessionExpires.valueOf() - new Date().valueOf())
    }
  }

  private storeUser(dto: LoggedUserDTO) {
    localStorage.setItem(LOGGED_USER_KEY, JSON.stringify(dto))
    this._user = toModel(dto)
    this.next()
    setTimeout(() => this.logout(), this._user.sessionExpires.valueOf() - new Date().valueOf())
  }

  private removeUser() {
    localStorage.removeItem(LOGGED_USER_KEY)
    this._user = undefined
    this.next()
  }

  userCan(perm: string): boolean {
    return this._user?.permissions?.includes(perm) || false
  }

  sendRecoverPassword(email: string): Observable<boolean> {
    return this._api.sendRecoverPassword(email)
  }
}
